import type { CSSProperties, Dispatch, SetStateAction } from 'react';
import Icon from '../ui/Icon';
import { S } from '../../config/theme.js';
import { DrpxLogo } from '../logo/DrpxLogo';
import type { Profile, TranslateFn } from '../../types';
import type { NavTab } from './renderBottomNav';
import { themeModeIcon, themeModeLabel, themeButtonTitle, type ThemeMode } from '../../lib/theme';
import { rollenLabelKey, rollenIcon } from '../../lib/zugang.js';

interface RenderMobileDrawerProps {
  user: Profile;
  darkMode: boolean;
  themeMode: ThemeMode;
  cycleThemeMode: () => void;
  lang: string;
  setLang: (l: string) => void;
  langOpen: boolean;
  setLangOpen: Dispatch<SetStateAction<boolean>>;
  LANGS: readonly string[];
  LANG_LABELS: Record<string, string>;
  LANG_FLAGS: Record<string, string>;
  /** Aktive Tab-ID. */
  tab: string;
  nav: (tabId: string) => void;
  navTabs: NavTab[];
  /** Ungelesen-Zähler pro Tab-ID. */
  navBadges: Record<string, number>;
  logout: () => void;
  setMobileMenu: Dispatch<SetStateAction<boolean>>;
  t: TranslateFn;
}

/**
 * renderMobileDrawer — Mobile Hamburger-Menü als Vollbild-Drawer mit
 * Navigation und Settings.
 *
 * Breiter als `renderBottomSheet` und zeigt die Tabs als vertikale Liste statt
 * als 3-Spalten-Grid. Unterstützt Divider-Einträge (`nt.divider === true`) als
 * Section-Überschriften mit optionalem Icon + Lock-Indicator.
 */
export function renderMobileDrawer({
  user,
  darkMode,
  themeMode,
  cycleThemeMode,
  lang,
  setLang,
  langOpen,
  setLangOpen,
  LANGS,
  LANG_LABELS,
  LANG_FLAGS,
  tab,
  nav,
  navTabs,
  navBadges,
  logout,
  setMobileMenu,
  t,
}: RenderMobileDrawerProps) {
  return (
    <div
      className="mobile-drawer"
      style={{
        position: 'fixed' as const,
        top: 0,
        right: 0,
        width: 280,
        maxWidth: '80vw',
        height: '100vh',
        background: 'var(--surface)',
        borderLeft: '1px solid var(--border)',
        zIndex: 201,
        padding: '20px',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 0,
        animation: 'dropdownSlide .25s cubic-bezier(.2,0,.1,1)',
        overflowY: 'auto' as const,
      }}>
      {/* Header */}
      <div
        style={{
          ...(S.rowBetween as CSSProperties),
          marginBottom: 20,
          paddingBottom: 16,
          borderBottom: '1px solid var(--border)',
        }}>
        <DrpxLogo size={0.4} dark={darkMode} />
        <button
          onClick={function () {
            setMobileMenu(false);
          }}
          style={{
            background: 'var(--surface2)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
            cursor: 'pointer',
            color: 'var(--muted)',
          }}>
          <Icon name="cross" size={16} />
        </button>
      </div>
      {/* User Info */}
      <div
        style={{
          ...(S.row as CSSProperties),
          gap: 10,
          padding: '12px 14px',
          background: 'var(--surface2)',
          borderRadius: 12,
          marginBottom: 16,
        }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: 'rgba(34,197,94,.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
          }}>
          <Icon
            name={rollenIcon(user)}
            size={18} />
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>
            {user.firma || user.name}
          </div>
          <div className="t-muted-sm">
            {rollenLabelKey(user) ? t(rollenLabelKey(user) as string) : 'Admin'}
          </div>
        </div>
      </div>
      {/* Nav Items */}
      <div style={{ ...(S.col as CSSProperties), gap: 2, flex: 1 }}>
        {navTabs.map(function (nt, i) {
          return nt.divider
            ? <div
                key={'d' + i}
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--accent)',
                  textTransform: 'uppercase' as const,
                  letterSpacing: 1,
                  padding: '10px 14px 6px',
                  marginTop: i > 0 ? 10 : 0,
                  borderTop: i > 0 ? '1px solid var(--border)' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}>
                <span
                  style={{ width: 3, height: 16, borderRadius: 2, background: 'var(--accent)', flexShrink: 0 }} />
                {nt.divIcon && <Icon name={nt.divIcon} size={12} />}
                {nt.label}
                {nt.locked && <Icon name="lock" size={10} />}
              </div>
            : <button
                key={nt.id}
                onClick={function () {
                  nav(nt.id);
                  setMobileMenu(false);
                }}
                style={{
                  ...(S.row as CSSProperties),
                  gap: 12,
                  padding: '12px 14px',
                  border: 'none',
                  background: tab === nt.id ? 'rgba(74,222,128,.1)' : 'transparent',
                  color: tab === nt.id ? 'var(--accent)' : 'var(--text)',
                  fontFamily: "'DM Sans',sans-serif",
                  fontSize: 14,
                  cursor: 'pointer',
                  borderRadius: 10,
                  fontWeight: tab === nt.id ? 700 : 400,
                  textAlign: 'left' as const,
                  transition: 'all .15s',
                  borderLeft: tab === nt.id ? '3px solid var(--accent)' : '3px solid transparent',
                }}>
                <span
                  style={{
                    width: 24,
                    textAlign: 'center' as const,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  <Icon name={nt.icon} size={18} />
                </span>
                {' '}
                {nt.label}
                {navBadges[nt.id] > 0 &&
                  <span
                    style={{
                      background: nt.id === 'admin-users' ? '#f59e0b' : '#ef4444',
                      color: '#fff',
                      borderRadius: 20,
                      padding: '0 5px',
                      fontSize: 9,
                      fontWeight: 700,
                      minWidth: 16,
                      textAlign: 'center' as const,
                      lineHeight: '16px',
                      marginLeft: 'auto',
                    }}>
                    {navBadges[nt.id] > 99 ? '99+' : navBadges[nt.id]}
                  </span>}
                {nt.locked && <Icon name="lock" size={10} color="var(--muted)" />}
              </button>;
        })}
      </div>
      {/* Footer */}
      <div
        style={{
          borderTop: '1px solid var(--border)',
          paddingTop: 16,
          marginTop: 12,
          display: 'flex',
          flexDirection: 'column' as const,
          gap: 8,
        }}>
        <div className="flex-gap-8">
          <button
            onClick={cycleThemeMode}
            title={themeButtonTitle(themeMode, t)}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              background: 'var(--surface2)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: '10px',
              fontSize: 13,
              cursor: 'pointer',
              color: 'var(--text)',
            }}>
            <Icon name={themeModeIcon(themeMode)} size={16} color={themeMode === 'light' ? undefined : 'var(--text)'} />
            {themeModeLabel(themeMode, t)}
          </button>
          <button
            onClick={function () {
              setLangOpen(function (o) {
                return !o;
              });
            }}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              background: 'var(--surface2)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: '10px',
              fontSize: 13,
              cursor: 'pointer',
              color: 'var(--text)',
            }}>
            {LANG_FLAGS[lang]}
            {' '}
            {LANG_LABELS[lang]}
          </button>
        </div>
        {langOpen &&
          <div style={{ ...(S.rowWrap as CSSProperties), gap: 4 }}>
            {LANGS.map(function (l) {
              return (
                <button
                  key={l}
                  onClick={function () {
                    setLang(l);
                    setLangOpen(false);
                  }}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid ' + (l === lang ? 'var(--accent)' : 'var(--border)'),
                    background: l === lang ? 'rgba(74,222,128,.1)' : 'var(--surface2)',
                    borderRadius: 8,
                    fontSize: 12,
                    cursor: 'pointer',
                    color: 'var(--text)',
                    fontWeight: l === lang ? 700 : 400,
                  }}>
                  {LANG_FLAGS[l]}
                  {' '}
                  {LANG_LABELS[l]}
                </button>
              );
            })}
          </div>}
        <button
          onClick={function () {
            logout();
            setMobileMenu(false);
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            background: 'rgba(239,68,68,.08)',
            border: '1px solid rgba(239,68,68,.2)',
            borderRadius: 10,
            padding: '12px',
            fontSize: 13,
            cursor: 'pointer',
            color: 'var(--danger)',
            fontWeight: 600,
          }}>
          <Icon name="key" size={14} color="var(--danger)" />
          {t('abmelden')}
        </button>
      </div>
    </div>
  );
}
